import { z } from 'zod/v4';

/**
 * Leaves for the legacy (pre-versioning) schemas, read the way the old renderer read a stored value:
 * it never validated, it used whatever was there. A value of the wrong type therefore broke nothing on
 * the published page, and must not fail its whole entity here — a column that fails is hidden in the
 * editor and left off the published page. So a value is converted where the old renderer effectively
 * converted it, and read as absent — the migration's default — where it could not have meant anything.
 */

const numeric = (value: unknown): unknown =>
    typeof value === 'string' && value.trim() !== '' && Number.isFinite(Number(value)) ? Number(value) : value;

/** A number, or a numeric string: `'6'` sized a column exactly as `6` did. */
export const legacyNumber = () => z.preprocess(numeric, z.number().nullish()).catch(undefined);

/** By truthiness, as the old renderer's `if (data.isBorder)` read it — so `'false'` is on. */
export const legacyBoolean = () =>
    z.preprocess(
        (value) => (value == null || typeof value === 'boolean' ? value : Boolean(value)),
        z.boolean().nullish()
    );

/** A string; a number is written out as one. */
export const legacyString = () =>
    z.preprocess((value) => (typeof value === 'number' ? String(value) : value), z.string().nullish()).catch(undefined);

/** One of the values the old renderer had a class for — any other drew nothing. */
export const legacyEnum = <const T extends readonly [string, ...string[]]>(values: T) =>
    z.enum(values).nullish().catch(undefined);

/** A nested bag that does not read is left out, rather than failing the entity it sits in. */
export const legacyBag = <T extends z.ZodType>(schema: T) => schema.nullish().catch(undefined);

/** A JSS value — `string | number`, as the host wrote styles. */
export const legacyJssValue = () => z.union([z.string(), z.number()]).nullish().catch(undefined);
